// src/OrgPlansAndRoi.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

const THEME = {
  primaryBg: 'bg-sky-500',
  primaryText: 'text-white',
  primaryBorder: 'border-sky-500',
  mutedText: 'text-gray-600',
  cardBg: 'bg-white',
  cardBorder: 'border border-slate-200',
  shadow: 'shadow-sm',
  pill: 'rounded-xl',
  radius: 'rounded-xl',
};

function currency(n, frac = 0, curr = 'USD') {
  const num = Number(n || 0);
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: curr || 'USD',
    minimumFractionDigits: frac,
    maximumFractionDigits: frac,
  });
}
function currency2(n, curr) {
  return currency(n, 2, curr);
}

export default function OrgPlansAndRoi() {
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ teachers: 0, students: 0, monthlyVisits: 0 });
  const [error, setError] = useState('');

  // pricing / ROI inputs
  const [providers, setProviders] = useState(10);
  const [activeChildren, setActiveChildren] = useState(200);
  const [workdaysPerMonth, setWorkdaysPerMonth] = useState(20);
  const [visitsPerProviderPerDay, setVisitsPerProviderPerDay] = useState(5);
  const [baselineMilesPerVisit, setBaselineMilesPerVisit] = useState(7);
  const [routeReductionPct, setRouteReductionPct] = useState(18);
  const [costPerMile, setCostPerMile] = useState(0.725);
  const [adminMinutesSavedPerVisit, setAdminMinutesSavedPerVisit] = useState(6);
  const [adminHourlyRate, setAdminHourlyRate] = useState(28);

  // koji plan koristimo kao referentni za ROI (license cost)
  const [roiReferencePlanKey, setRoiReferencePlanKey] = useState(null);

  // modal za slanje zahteva
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestPlan, setRequestPlan] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [requestSending, setRequestSending] = useState(false);
  const [toast, setToast] = useState('');

  const currencyCode = organization?.currency || 'USD';

  useEffect(() => {
    let isMounted = true;
    async function loadData() {
      setLoading(true);
      setError('');
      try {
        const res = await API.get('/org/plans');
        if (!isMounted) return;
        const { organization, plans, stats } = res.data || {};
        setOrganization(organization || null);
        setPlans(plans || []);
        setStats(stats || { teachers: 0, students: 0, monthlyVisits: 0 });

        // inicijalno popuni inpute iz realnih brojeva
        if (stats?.teachers) setProviders(stats.teachers);
        if (stats?.students) setActiveChildren(stats.students);

        // referentni plan = trenutni billing_plan (uvek krećemo od realnog)
        if (organization?.billing_plan) {
          setRoiReferencePlanKey(organization.billing_plan);
        } else if (plans && plans.length) {
          setRoiReferencePlanKey(plans[0].plan_key);
        }
      } catch (err) {
        console.error('/org/plans FE error', err);
        if (isMounted) setError('Failed to load plan data.');
      } finally {
        if (isMounted) setLoading(false);
      }
    }
    loadData();
    return () => {
      isMounted = false;
    };
  }, []);

  // helper: izračunaj mesečni trošak za konkretan plan
  function getPlanMonthlyCost(plan, useOrgOverrides) {
    if (!plan || !organization) return NaN;

    const t = providers;
    const s = activeChildren;

    const isCurrent = plan.plan_key === organization.billing_plan;
    // Ako je current plan i useOrgOverrides = true -> koristi override vrednosti iz organizations
    const baseFee =
      useOrgOverrides && isCurrent && organization.base_fee != null
        ? Number(organization.base_fee)
        : Number(plan.default_base_fee || 0);

    const perTeacher =
      useOrgOverrides && isCurrent && organization.price_per_teacher != null
        ? Number(organization.price_per_teacher)
        : Number(plan.default_price_per_teacher || 0);

    const perStudent =
      useOrgOverrides && isCurrent && organization.price_per_student != null
        ? Number(organization.price_per_student)
        : Number(plan.default_price_per_student || 0);

    return baseFee + perTeacher * t + perStudent * s;
  }

  // map plan_key -> monthly cost
  const planCosts = useMemo(() => {
    const out = {};
    plans.forEach(p => {
      const useOrgOverrides = true;
      out[p.plan_key] = getPlanMonthlyCost(p, useOrgOverrides);
    });
    return out;
  }, [plans, providers, activeChildren, organization]);

  const totalMonthlyVisits = useMemo(
    () => providers * visitsPerProviderPerDay * workdaysPerMonth,
    [providers, visitsPerProviderPerDay, workdaysPerMonth]
  );

  const milesSavedPerVisit = useMemo(
    () => baselineMilesPerVisit * (routeReductionPct / 100),
    [baselineMilesPerVisit, routeReductionPct]
  );

  const monthlyMilesSaved = useMemo(
    () => totalMonthlyVisits * milesSavedPerVisit,
    [totalMonthlyVisits, milesSavedPerVisit]
  );

  const monthlyMilesSavingsUSD = useMemo(
    () => monthlyMilesSaved * costPerMile,
    [monthlyMilesSaved, costPerMile]
  );

  const monthlyAdminHoursSaved = useMemo(() => {
    const minutes = totalMonthlyVisits * adminMinutesSavedPerVisit;
    return minutes / 60;
  }, [totalMonthlyVisits, adminMinutesSavedPerVisit]);

  const monthlyAdminSavingsUSD = useMemo(
    () => monthlyAdminHoursSaved * adminHourlyRate,
    [monthlyAdminHoursSaved, adminHourlyRate]
  );

  const monthlyGrossSavings = useMemo(
    () => monthlyMilesSavingsUSD + monthlyAdminSavingsUSD,
    [monthlyMilesSavingsUSD, monthlyAdminSavingsUSD]
  );

  const chosenMonthlyCost = useMemo(() => {
    if (!roiReferencePlanKey) return 0;
    const val = planCosts[roiReferencePlanKey];
    return Number.isFinite(val) ? val : 0;
  }, [roiReferencePlanKey, planCosts]);

  const netMonthlyBenefit = useMemo(
    () => monthlyGrossSavings - chosenMonthlyCost,
    [monthlyGrossSavings, chosenMonthlyCost]
  );

  const monthlyROI = useMemo(() => {
    const cost = chosenMonthlyCost;
    if (cost <= 0) return 0;
    return (netMonthlyBenefit / cost) * 100;
  }, [netMonthlyBenefit, chosenMonthlyCost]);

  // otvaranje modala – ovde odmah prebacujemo ROI referencu na taj plan
  function openRequestModal(plan) {
    if (!plan || !organization) return;
    setRequestPlan(plan);
    setRoiReferencePlanKey(plan.plan_key);

    const roughCost = planCosts[plan.plan_key] || 0;
    const defaultMsg =
      `Hi,\n\n` +
      `We would like to discuss switching our plan from "${organization.billing_plan}" to "${plan.plan_key}" (${plan.plan_name}).\n\n` +
      `Scenario with our current numbers:\n` +
      `- Providers (teachers): ${providers}\n` +
      `- Active children/month: ${activeChildren}\n` +
      `- Estimated license cost for this plan: ${currency2(roughCost, currencyCode)}/month\n\n` +
      `Please let us know if this plan is a good fit and what the next steps would be.\n\n` +
      `Thank you,`;
    setRequestMessage(defaultMsg);
    setRequestModalOpen(true);
  }

  async function sendRequest() {
    if (!requestPlan) return;
    setRequestSending(true);
    setToast('');
    try {
      await API.post('/org/plan-request', {
        target_plan_key: requestPlan.plan_key,
        message: requestMessage,
        context: {
          providers,
          activeChildren,
          workdaysPerMonth,
          visitsPerProviderPerDay,
          baselineMilesPerVisit,
          routeReductionPct,
          costPerMile,
          adminMinutesSavedPerVisit,
          adminHourlyRate,
          totalMonthlyVisits,
          monthlyMilesSaved,
          monthlyMilesSavingsUSD,
          monthlyAdminHoursSaved,
          monthlyAdminSavingsUSD,
          monthlyGrossSavings,
          chosenMonthlyCost,
          netMonthlyBenefit,
          monthlyROI,
        },
      });
      setToast('Plan change request has been sent to the super admin.');
      setRequestModalOpen(false);
    } catch (err) {
      console.error('/org/plan-request FE error', err);
      setToast('Failed to send plan change request.');
    } finally {
      setRequestSending(false);
    }
  }

  // auto-isključi toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  if (loading) {
    return (
      <div className="p-4">
        <div className="animate-pulse text-gray-500 text-sm">Loading plans…</div>
      </div>
    );
  }

  if (error) {
    return <div className="p-4 text-red-600 text-sm">{error}</div>;
  }

  if (!organization) {
    return <div className="p-4 text-sm text-gray-600">Organization not found.</div>;
  }

  const currentPlanKey = organization.billing_plan;
  const currentPlan = plans.find(p => p.plan_key === currentPlanKey) || null;
  const currentPlanCost = currentPlanKey ? planCosts[currentPlanKey] || 0 : 0;
  const roiPlan = plans.find(p => p.plan_key === roiReferencePlanKey) || null;

  // --- mali helperi za hint ispod kartica ---
  const trialPlan = plans.find(
    p => p.plan_key && p.plan_key.toLowerCase() === 'trial'
  );
  const basicPlan = plans.find(
    p => p.plan_key && p.plan_key.toLowerCase() === 'basic'
  );
  const proPlan = plans.find(
    p => p.plan_key && p.plan_key.toLowerCase() === 'pro'
  );
  const enterprisePlan = plans.find(
    p => p.plan_key && p.plan_key.toLowerCase() === 'enterprise'
  );

  return (
    <div className="max-w-6xl mx-auto px-4 py-4 text-sm text-slate-900">
      {/* toast */}
      {toast && (
        <div className="fixed inset-0 flex items-start justify-center mt-6 pointer-events-none z-50">
          <div className="bg-white border border-gray-300 rounded-xl shadow-lg px-4 py-3 text-xs md:text-sm text-gray-800 pointer-events-auto">
            {toast}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl md:text-2xl font-semibold tracking-tight">
          Plan & ROI overview
        </h1>
        <p className={`${THEME.mutedText} mt-1 text-xs md:text-sm leading-snug`}>
          See your current subscription, compare estimated license costs per plan, and get a rough ROI estimate
          based on routing and admin time savings. Nothing changes automatically — plan updates always go
          through a short conversation with the super admin.
        </p>

        <div className="mt-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              Organization
            </div>
            <div className="inline-flex items-center gap-2">
              <span className="font-medium text-sm">{organization.name}</span>
              <span className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-sky-500 text-white text-[11px] uppercase tracking-wide">
                Current plan: {currentPlanKey || 'n/a'}
              </span>
            </div>
            <div className="mt-1 text-[11px] text-gray-500">
              Currency: <b>{currencyCode}</b> · Active teachers: {stats.teachers} · Active children: {stats.students}
            </div>
          </div>

          {/* mali "Current vs scenario" summary blok */}
          <div className="inline-flex flex-col gap-1 px-3 py-2 rounded-xl border border-slate-200 bg-slate-50 text-[11px] md:text-xs">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-600">Current plan estimate</span>
              <span className="font-semibold">
                {Number.isFinite(currentPlanCost)
                  ? `${currency2(currentPlanCost, currencyCode)}/mo`
                  : 'n/a'}
              </span>
            </div>
            {roiPlan && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-gray-600">
                  ROI reference: <b>{roiPlan.plan_key}</b>
                </span>
                <span className="font-semibold">
                  {currency2(chosenMonthlyCost, currencyCode)}/mo
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PLANS: horizontal scroll na mobilnom, grid na desktopu */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
            Available plans
          </h2>
          <span className="text-[11px] text-gray-500">
            Estimates are based on <b>{providers}</b> teachers & <b>{activeChildren}</b> active children / month.
          </span>
        </div>

        <div className="md:hidden -mx-4 overflow-x-auto pb-2">
          <div className="flex gap-3 px-4">
            {plans.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrent={plan.plan_key === currentPlanKey}
                isReference={plan.plan_key === roiReferencePlanKey}
                monthlyCost={planCosts[plan.plan_key]}
                currencyCode={currencyCode}
                onRequest={() => openRequestModal(plan)}
                onSetReference={() => setRoiReferencePlanKey(plan.plan_key)}
              />
            ))}
          </div>
        </div>

        {/* sužen layout: 2 u redu na md, 4 u redu na lg */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map(plan => (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={plan.plan_key === currentPlanKey}
              isReference={plan.plan_key === roiReferencePlanKey}
              monthlyCost={planCosts[plan.plan_key]}
              currencyCode={currencyCode}
              onRequest={() => openRequestModal(plan)}
              onSetReference={() => setRoiReferencePlanKey(plan.plan_key)}
            />
          ))}
        </div>

        {/* HINT ispod kartica */}
        {plans.length > 0 && (
          <div className="mt-3 text-[11px] text-gray-500 leading-snug">
            {trialPlan ? (
              <>
                <span className="font-semibold">Tip:</span>{' '}
                <b>{trialPlan.plan_name}</b> is ideal for a small pilot
                {trialPlan.default_max_teachers != null && (
                  <>
                    {' '}up to <b>{trialPlan.default_max_teachers}</b> providers
                  </>
                )}
                {trialPlan.default_max_students != null && (
                  <>
                    {' '}and <b>{trialPlan.default_max_students}</b> active children
                  </>
                )}
                . For day-to-day use with a full team, most agencies move to{' '}
                {basicPlan ? <b>{basicPlan.plan_name}</b> : <b>our standard plan</b>}
                {proPlan && (
                  <>
                    {' '}or <b>{proPlan.plan_name}</b>
                  </>
                )}
                .
                {enterprisePlan && (
                  <>
                    {' '}If you are multi-site or have 50+ providers,{' '}
                    <b>{enterprisePlan.plan_name}</b> is built for that scale.
                  </>
                )}
              </>
            ) : (
              <>
                <span className="font-semibold">Tip:</span>{' '}
                Start with a smaller plan for your pilot, and once routing + reports become part of the
                daily workflow, move up to the next tier so providers and admins don’t hit usage limits.
              </>
            )}
          </div>
        )}
      </div>

      {/* OPTIONAL ONE-TIME SERVICES */}
      <div className="mb-8">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
          Optional one-time services (outside monthly plans)
        </h2>
        <p className={`${THEME.mutedText} text-[11px] mt-1 leading-snug`}>
          These services help you start faster. They are billed once per organization and can be tailored
          to your size and workflows.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
          {/* Migration & discipline setup */}
          <Card>
            <h3 className="text-sm font-semibold">Migration & discipline setup</h3>
            <div className="mt-1 text-xs text-gray-600 leading-snug">
              We migrate your active caseload, providers and basic schedule data from spreadsheets or legacy
              systems, and configure your EI / PI / ABA / OT / PT disciplines, domains and skills to match
              your agency model.
            </div>
            <div className="mt-2 text-xs text-gray-700">
              Typical package for small agencies:
            </div>
            <div className="mt-0.5 text-lg font-semibold">
              {currency2(1399, currencyCode)}{' '}
              <span className="text-xs font-normal text-gray-500">one-time</span>
            </div>
          </Card>

          {/* Training package */}
          <Card>
            <h3 className="text-sm font-semibold">Live training for admins & teachers</h3>
            <div className="mt-1 text-xs text-gray-600 leading-snug">
              Remote live sessions for org admins and providers covering scheduling, routing, time logs,
              skills, reports and telehealth workflows, plus Q&amp;A and recorded materials for new hires.
            </div>
            <div className="mt-2 text-xs text-gray-700">
              Typical package for small agencies:
            </div>
            <div className="mt-0.5 text-lg font-semibold">
              {currency2(1699, currencyCode)}{' '}
              <span className="text-xs font-normal text-gray-500">one-time</span>
            </div>
          </Card>
        </div>
      </div>

      {/* ROI INPUTS + SUMMARY */}
      <div className="mt-8">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-600">
            ROI estimate
          </h2>
          {roiPlan && (
            <span className="text-[11px] text-gray-500">
              ROI reference: <b>{roiPlan.plan_name}</b> ({roiPlan.plan_key}) ·{' '}
              {currency2(chosenMonthlyCost, currencyCode)}/month
            </span>
          )}
        </div>
        <p className={`${THEME.mutedText} text-xs mb-3 leading-snug`}>
          Adjust the numbers below to match your operations. We estimate monthly savings from shorter routes and
          less admin work, and compare that with the license cost of the reference plan. This is only a rough
          model — not a binding quote.
        </p>

        {/* referentni plan toggle (kompaktan) */}
        <div className="mb-4 p-3 border border-slate-200 rounded-xl bg-white">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-medium text-gray-800">
              Choose ROI reference plan:
            </span>
            <div className="flex flex-wrap gap-1">
              {plans.map(p => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setRoiReferencePlanKey(p.plan_key)}
                  className={[
                    'px-2.5 py-1 text-[11px] border',
                    THEME.pill,
                    roiReferencePlanKey === p.plan_key
                      ? 'bg-sky-500 text-white border-sky-500'
                      : 'bg-white text-gray-800 border-slate-200',
                  ].join(' ')}
                >
                  {p.plan_key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* grid inputa */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <Label>Providers (teachers)</Label>
            <InputNumber value={providers} setValue={setProviders} min={1} />
            <Label className="mt-3">Active children / month</Label>
            <InputNumber value={activeChildren} setValue={setActiveChildren} min={0} />
            <Label className="mt-3">Working days / month</Label>
            <InputNumber value={workdaysPerMonth} setValue={setWorkdaysPerMonth} min={1} />
          </Card>

          <Card>
            <Label>Visits per provider / day</Label>
            <InputNumber value={visitsPerProviderPerDay} setValue={setVisitsPerProviderPerDay} min={1} />
            <Label className="mt-3">Avg miles / visit (before)</Label>
            <InputNumber
              value={baselineMilesPerVisit}
              setValue={setBaselineMilesPerVisit}
              min={0}
              step={0.1}
            />
            <Label className="mt-3">% miles reduction (routing)</Label>
            <InputNumber
              value={routeReductionPct}
              setValue={setRouteReductionPct}
              min={0}
              max={100}
              step={1}
            />
          </Card>

          <Card>
            <Label>Cost per mile ({currencyCode})</Label>
            <InputNumber value={costPerMile} setValue={setCostPerMile} min={0} step={0.001} />
            <Label className="mt-3">Admin time saved (min/visit)</Label>
            <InputNumber
              value={adminMinutesSavedPerVisit}
              setValue={setAdminMinutesSavedPerVisit}
              min={0}
            />
            <Label className="mt-3">Admin hourly rate ({currencyCode}/h)</Label>
            <InputNumber
              value={adminHourlyRate}
              setValue={setAdminHourlyRate}
              min={0}
              step={1}
            />
          </Card>
        </div>

        {/* rezultat */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
          <Card>
            <h4 className="font-semibold text-sm">Mileage savings</h4>
            <p className="text-xs text-gray-700 mt-1 leading-snug">
              Reduced miles / month:{' '}
              <b>{Math.round(monthlyMilesSaved).toLocaleString()}</b>
              <br />
              In money:{' '}
              <b>{currency2(monthlyMilesSavingsUSD, currencyCode)}</b>
            </p>
          </Card>
          <Card>
            <h4 className="font-semibold text-sm">Admin savings</h4>
            <p className="text-xs text-gray-700 mt-1 leading-snug">
              Admin hours saved / month:{' '}
              <b>{monthlyAdminHoursSaved.toFixed(1)}</b>
              <br />
              In money:{' '}
              <b>{currency2(monthlyAdminSavingsUSD, currencyCode)}</b>
            </p>
          </Card>
          <Card>
            <h4 className="font-semibold text-sm">Net benefit & ROI</h4>
            <p className="text-xs text-gray-700 mt-1 leading-snug">
              Gross savings / month:{' '}
              <b>{currency2(monthlyGrossSavings, currencyCode)}</b>
              <br />
              License cost (ref. plan):{' '}
              <b>{currency2(chosenMonthlyCost, currencyCode)}</b>
              <br />
              Net monthly benefit:{' '}
              <b>{currency2(netMonthlyBenefit, currencyCode)}</b>
              <br />
              ROI:{' '}
              <b>{Number.isFinite(monthlyROI) ? monthlyROI.toFixed(0) : 0}%</b>
            </p>
          </Card>
        </div>
      </div>

      {/* MODAL za promenu plana */}
      {requestModalOpen && requestPlan && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 text-sm">
            <h3 className="text-base font-semibold mb-1">Request plan change</h3>
            <p className="text-xs text-gray-600 mb-3">
              You are currently on <b>{organization.billing_plan}</b>. You are asking about{' '}
              <b>
                {requestPlan.plan_key} ({requestPlan.plan_name})
              </b>
              . This will notify the super admin so you can finalize details together.
            </p>

            <label className="block text-xs font-medium text-gray-700 mb-1">
              Message to super admin
            </label>
            <textarea
              rows={8}
              className="w-full border rounded-xl px-3 py-2 text-xs"
              value={requestMessage}
              onChange={e => setRequestMessage(e.target.value)}
            />

            <div className="mt-4 flex justify-end gap-3">
              <button
                type="button"
                className="px-4 py-2 text-xs rounded-xl border bg-white"
                onClick={() => setRequestModalOpen(false)}
                disabled={requestSending}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-xs ${THEME.pill} ${THEME.primaryBg} ${THEME.primaryText}`}
                onClick={sendRequest}
                disabled={requestSending}
              >
                {requestSending ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- helpers / UI ---------- */

function Card({ children }) {
  return (
    <div className={`${THEME.radius} ${THEME.cardBorder} ${THEME.shadow} ${THEME.cardBg} p-4`}>
      {children}
    </div>
  );
}

function Label({ children, className = '' }) {
  return (
    <label className={`block text-xs font-medium text-gray-700 mb-1 ${className}`}>
      {children}
    </label>
  );
}

function InputNumber({ value, setValue, min, max, step = 1 }) {
  return (
    <input
      type="number"
      value={value}
      min={min}
      max={max}
      step={step}
      onChange={e => setValue(safeNum(e.target.value, { min, max }))}
      className="w-full rounded-lg border px-3 py-1.5 text-xs"
    />
  );
}

function safeNum(v, { min, max }) {
  let n = Number(v);
  if (!Number.isFinite(n)) n = 0;
  if (typeof min === 'number') n = Math.max(min, n);
  if (typeof max === 'number') n = Math.min(max, n);
  return n;
}

function PlanCard({
  plan,
  isCurrent,
  isReference,
  monthlyCost,
  currencyCode,
  onRequest,
  onSetReference,
}) {
  const hasLimits =
    plan.default_max_teachers != null ||
    plan.default_max_students != null ||
    plan.default_max_monthly_visits != null;

  const key = (plan.plan_key || '').toLowerCase();
  const hasPayroll = key === 'pro' || key === 'enterprise';
  const hasTraining = key === 'enterprise';
  const hasMessaging = key === 'pro' || key === 'enterprise';

  return (
    <div
      className={[
        THEME.radius,
        THEME.cardBorder,
        THEME.shadow,
        THEME.cardBg,
        'p-4 flex flex-col min-w-[220px] text-xs',
        isCurrent ? 'ring-2 ring-sky-500' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold leading-snug">{plan.plan_name}</h3>
          <div className="mt-1 flex items-center gap-1">
            <span
              className={[
                'px-1.5 py-0.5 text-[10px] font-semibold rounded-full uppercase tracking-wide',
                'bg-gray-100 text-gray-700',
              ].join(' ')}
            >
              {plan.plan_key}
            </span>
            {isCurrent && (
              <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-emerald-100 text-emerald-800 font-medium">
                Current
              </span>
            )}
            {isReference && !isCurrent && (
              <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-sky-500 text-white font-medium">
                ROI ref
              </span>
            )}
          </div>
        </div>
      </div>

      {plan.description && (
        <p className={`${THEME.mutedText} mt-2 text-[11px] leading-snug line-clamp-3`}>
          {plan.description}
        </p>
      )}

      <div className="mt-3">
        <div className="text-[11px] text-gray-500">Estimated license with your numbers</div>
        <div className="text-lg font-semibold mt-0.5">
          {Number.isFinite(monthlyCost)
            ? `${currency2(monthlyCost, currencyCode)}/mo`
            : 'On request'}
        </div>
      </div>

      {hasLimits && (
        <ul className="mt-3 text-[11px] text-gray-700 space-y-0.5 list-disc pl-4">
          {plan.default_max_teachers != null && (
            <li>Up to {plan.default_max_teachers} teachers (default)</li>
          )}
          {plan.default_max_students != null && (
            <li>Up to {plan.default_max_students} active children (default)</li>
          )}
          {plan.default_max_monthly_visits != null && (
            <li>Up to {plan.default_max_monthly_visits} visits / month (default)</li>
          )}
        </ul>
      )}

      {/* Core capabilities – svi planovi */}
      <div className="mt-3">
        <div className="text-[11px] font-semibold text-gray-700 mb-0.5">
          Core capabilities
        </div>
        <ul className="text-[11px] text-gray-700 space-y-0.5 list-disc pl-4">
          <li>Admin, teacher & parent portals</li>
          <li>Full EI / PI / ABA / OT / PT discipline support</li>
          <li>Custom disciplines, domains & skills</li>
          <li>Smart skills engine for child progress tracking</li>
          <li>Training & resource docs for parents and staff</li>
          <li>Consent & contract documents per child in secure storage</li>
          <li>GPS check-in/out & G-Sign visit confirmation</li>
          <li>Visit scheduling by student and by teacher</li>
          <li>Smart routing & mileage between visits</li>
          <li>Best-teacher suggestion (rating, cost, distance)</li>
          <li>Next-child suggestion after each visit</li>
          <li>Smart handling of cancelled visits</li>
          
          <li>Progress reports for funders, agency & parents</li>
        </ul>
      </div>

      {/* Advanced – razlikuje Pro / Enterprise */}
      <div className="mt-3 border-t border-slate-200 pt-2">
        <div className="text-[11px] font-semibold text-gray-700 mb-0.5">
          Advanced
        </div>
        <ul className="text-[11px] space-y-0.5 list-disc pl-4">

	  
    <li
      className={
        hasPayroll ? 'text-gray-700' : 'text-gray-400 line-through'
      }
    >
      Objective vs subjective time logs
      {!hasPayroll && (
        <span className="ml-1 text-[10px] text-gray-400">
          (Pro & Enterprise)
        </span>
      )}
    </li>
          <li
            className={
              hasPayroll ? 'text-gray-700' : 'text-gray-400 line-through'
            }
          >
            Teacher work reports & payroll
            {!hasPayroll && (
              <span className="ml-1 text-[10px] text-gray-400">
                (Pro & Enterprise)
              </span>
            )}
          </li>

          <li
            className={
              hasMessaging ? 'text-gray-700' : 'text-gray-400 line-through'
            }
          >
            Secure in-app messaging between admins, teachers & parents
            {!hasMessaging && (
              <span className="ml-1 text-[10px] text-gray-400">
                (Pro & Enterprise)
              </span>
            )}
          </li>

          <li
            className={
              hasTraining ? 'text-gray-700' : 'text-gray-400 line-through'
            }
          >
            Training & evaluation system for new teachers
            {!hasTraining && (
              <span className="ml-1 text-[10px] text-gray-400">
                (Enterprise)
              </span>
            )}
          </li>
        </ul>
      </div>

      <div className="mt-auto pt-3 flex flex-col gap-1.5">
        <button
          type="button"
          className={`w-full ${THEME.pill} ${
            isCurrent
              ? 'bg-gray-100 text-gray-600 cursor-default'
              : `${THEME.primaryBg} ${THEME.primaryText}`
          } py-2 text-xs font-semibold`}
          onClick={!isCurrent ? onRequest : undefined}
          disabled={isCurrent}
        >
          {isCurrent ? 'Current plan' : 'Request this plan'}
        </button>

        {!isCurrent && (
          <button
            type="button"
            className="w-full text-[11px] text-gray-600 underline underline-offset-2"
            onClick={onSetReference}
          >
            Use as ROI reference
          </button>
        )}
      </div>
    </div>
  );
}






